/**
 * WB4 LLM-PALETTE — debounced /api/ai/suggest augmentation for command palette NL queries.
 *
 * Never auto-executes — gateway always requiresHumanApproval; palette shows disclosure only.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { omitUndefined } from '@shared/utils/omitUndefined';
import { resolveApiOrigin } from '@shared/api/resolve-api-origin';
import type { WorkbenchCommandContext } from '@shared/business-workbench/command-resolver';
import type { ResolvedCommand } from '@shared/business-workbench/command-resolver';
import {
  buildPaletteLlmDisclosureCommand,
  resolvePaletteLlmCommands,
  type PaletteLlmSuggestPayload,
} from '@shared/business-workbench/workbench-palette-llm-resolver';
import { fetchWithAuth } from '../services/authFetch';
import { buildAssistiveGatewayPayload } from '../components/workbench/ai/build-assistive-payload';

const DEFAULT_DEBOUNCE_MS = 400;
const MIN_QUERY_LENGTH = 2;

export interface PaletteLlmAugmentationResult {
  loading: boolean;
  stub?: boolean;
  commands: ResolvedCommand[];
  disclosure?: ResolvedCommand;
}

interface AssistiveSuggestApiData {
  taskDraft?: { summary?: string; note?: string };
  suggestedActionIds?: readonly string[];
  filterHandlerRef?: string;
  requiresHumanApproval?: true;
  stub?: boolean;
}

interface AssistiveApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface UsePaletteLlmAugmentationOptions {
  tenantId: string;
  query: string;
  ctx: WorkbenchCommandContext;
  enabled?: boolean;
  debounceMs?: number;
}

function resolvePaletteLlmAugmentationFromApi(
  data: AssistiveSuggestApiData,
  ctx: WorkbenchCommandContext
): PaletteLlmAugmentationResult {
  const payload: PaletteLlmSuggestPayload = omitUndefined({
    interpretationSummary: data.taskDraft?.summary,
    suggestedActionIds: data.suggestedActionIds,
    filterHandlerRef: data.filterHandlerRef,
    stub: data.stub ?? true,
  });

  return omitUndefined({
    loading: false,
    stub: payload.stub,
    commands: resolvePaletteLlmCommands(payload, ctx),
    disclosure: buildPaletteLlmDisclosureCommand(payload),
  });
}

export function usePaletteLlmAugmentation(
  options: UsePaletteLlmAugmentationOptions
): PaletteLlmAugmentationResult {
  const { tenantId, query, ctx, enabled = true, debounceMs = DEFAULT_DEBOUNCE_MS } = options;

  const trimmedQuery = query.trim();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PaletteLlmAugmentationResult>({
    loading: false,
    commands: [],
  });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeqRef = useRef(0);

  const shouldFetch =
    enabled && trimmedQuery.length >= MIN_QUERY_LENGTH && tenantId.trim().length > 0;

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!shouldFetch) {
      setLoading(false);
      setResult({ loading: false, commands: [] });
      return;
    }

    const requestSeq = ++requestSeqRef.current;

    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const baseUrl = resolveApiOrigin();
        const response = await fetchWithAuth(`${baseUrl}/api/ai/suggest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            buildAssistiveGatewayPayload({
              tenantId: tenantId.trim(),
              taskName: trimmedQuery,
              taskDescription: `palette NL query: ${trimmedQuery}`,
              sourceText: trimmedQuery,
              ...(ctx.businessDomain !== undefined ? { capabilityDomain: ctx.businessDomain } : {}),
              ...(ctx.object
                ? {
                    entity: {
                      id: ctx.object.id,
                      ...(ctx.object.name ? { name: ctx.object.name } : {}),
                      ...(ctx.status !== undefined ? { status: ctx.status } : {}),
                      ...(ctx.businessDomain !== undefined
                        ? { businessDomain: ctx.businessDomain }
                        : {}),
                    },
                  }
                : {}),
              ...(ctx.focus !== undefined ? { focus: ctx.focus } : {}),
            })
          ),
        });

        const body = (await response.json()) as AssistiveApiEnvelope<AssistiveSuggestApiData>;
        if (requestSeq !== requestSeqRef.current) {
          return;
        }

        if (!response.ok || !body.success || !body.data) {
          setResult({ loading: false, commands: [], stub: true });
          return;
        }

        setResult(resolvePaletteLlmAugmentationFromApi(body.data, ctx));
      } catch {
        if (requestSeq === requestSeqRef.current) {
          setResult({ loading: false, commands: [], stub: true });
        }
      } finally {
        if (requestSeq === requestSeqRef.current) setLoading(false);
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [shouldFetch, trimmedQuery, tenantId, ctx, debounceMs]);

  return useMemo(
    () => ({
      ...result,
      loading,
    }),
    [result, loading]
  );
}

/** @internal test helper */
export function __resolvePaletteLlmAugmentationFromApiForTest(
  data: AssistiveSuggestApiData,
  ctx: WorkbenchCommandContext
): PaletteLlmAugmentationResult {
  return resolvePaletteLlmAugmentationFromApi(data, ctx);
}
